/* import __COLOCATED_TEMPLATE__ from './plan-card.hbs'; */
/* RESPONSIBLE TEAM: team-pricing-and-packaging */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import {
  PLAN_DATA,
  P5_CORE_PLAN_UPGRADE_FEATURES,
  PROACTIVE_SUPPORT_PLUS_BASE_ID,
  AI_INSIGHTS_BASE_ID,
  PRICING_5_X_CORE_ESSENTIAL_ID,
} from 'embercom/lib/billing';
import type QuoteService from 'embercom/services/quote-service';
import type MarketingFeaturesService from 'embercom/services/marketing-features-service';
import type Plan from 'embercom/models/plan';
import { action } from '@ember/object';
import { ModalTypes } from './subscription-builder';
import moment from 'moment-timezone';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';

interface Args {
  plan: Plan;
  loading: boolean;
  selectedCorePlanId?: number;
  addOnIdsToAdd: Array<number>;
  setSelected: (plan: Plan) => void;
  paramsFeatureName: string;
  paramsPlanId: number;
  inSubscriptionPlan: Plan;
  isDisabled: boolean;
  showModal: (modalType: ModalTypes) => void;
  renewalDate?: string;
  scheduledDowngradeCorePlanId?: number;
  description?: string;
}

interface Signature {
  Args: Args;
  Element: never;
}

const PROACTIVE_SUPPORT_PLUS_BASE_ID_NUM = Number(PROACTIVE_SUPPORT_PLUS_BASE_ID);
const AI_INSIGHTS_BASE_ID_NUM = Number(AI_INSIGHTS_BASE_ID);

export default class PlanCard extends Component<Signature> {
  @service declare intl: any;
  @service declare appService: AppService;
  @service declare quoteService: QuoteService;
  @service declare marketingFeaturesService: MarketingFeaturesService;
  @service declare intercomEventService: any;
  @service declare aiAgentSetupService: AiAgentSetupService;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (this.isEssentialPlan) {
      this.aiAgentSetupService.reloadWorkflowsCounts();
    }
  }

  get renewalDate() {
    return this.args.renewalDate;
  }

  get featureName() {
    return this.args.paramsFeatureName;
  }

  get features() {
    if (this.isAddon) {
      let featuresRecord = this.marketingFeaturesService.getFeaturesByPlanId(
        this.args.plan.idAsNumber,
      );

      let featureTitles = Object.values(featuresRecord).map((feature) => feature.title);

      if (featureTitles.length > 0) {
        return featureTitles;
      }

      return PLAN_DATA[this.args.plan.idAsNumber]?.marketingFeaturesTranslationKeys;
    }

    let features = P5_CORE_PLAN_UPGRADE_FEATURES[this.args.plan.idAsNumber];
    if (features) {
      return [
        ...P5_CORE_PLAN_UPGRADE_FEATURES[this.args.plan.idAsNumber].left,
        ...P5_CORE_PLAN_UPGRADE_FEATURES[this.args.plan.idAsNumber].right,
      ];
    }

    return PLAN_DATA[this.args.plan.idAsNumber]?.marketingFeaturesTranslationKeys;
  }

  get isDowngrade() {
    //it's only a downgrade if its on the same product
    if (this.args.inSubscriptionPlan.product.id !== this.args.plan.product.id) {
      return false;
    }
    return this.args.inSubscriptionPlan.tierId > this.args.plan.tierId;
  }

  get hasFeature() {
    return (
      Number(this.args.paramsPlanId) === this.args.plan.idAsNumber && this.args.paramsFeatureName
    );
  }

  get isNotLowestTierCorePlan() {
    return this.args.plan.tierId > 1;
  }

  // prices
  get quote() {
    return this.quoteService.getQuoteById([this.args.plan.idAsNumber]);
  }

  get isSelectedCorePlan() {
    return this.args.selectedCorePlanId === this.args.plan.idAsNumber;
  }

  get isSelected() {
    return (
      // selected core plan, core plans can only be single selected
      this.isSelectedCorePlan ||
      // selected addons
      this.args.addOnIdsToAdd?.includes(this.args.plan.idAsNumber)
    );
  }

  get isProactiveSupportPlus() {
    return this.args.plan.idAsNumber === PROACTIVE_SUPPORT_PLUS_BASE_ID_NUM;
  }

  get isAiInsights() {
    return this.args.plan.idAsNumber === AI_INSIGHTS_BASE_ID_NUM;
  }

  get isAddon() {
    return this.isProactiveSupportPlus || this.isAiInsights;
  }

  get fullPrice() {
    if (this.isProactiveSupportPlus || this.isAiInsights) {
      return this.quote?.fullBreakdownAmount(this.args.plan.idAsNumber) || 0;
    }
    return (
      this.quote?.fullSeatPrice(this.args.plan.idAsNumber, this.args.plan.product.pricingMetric) ||
      0
    );
  }

  get planDaysRemainingOnTrial() {
    if (!this.args.plan.activeTrial) {
      return 0;
    }
    let trialEnd = moment(this.args.plan.activeTrialEndedAt);
    return trialEnd.diff(moment(), 'days');
  }

  get discountedSeatPrice() {
    if (this.isProactiveSupportPlus || this.isAiInsights) {
      return this.quote?.fullBreakdownAmount(this.args.plan.idAsNumber) || 0;
    }
    return (
      this.quote?.discountedAnnualSeatPrice(
        this.args.plan.idAsNumber,
        this.args.plan.product.pricingMetric,
      ) || 0
    );
  }

  get proactiveSupportBaseUsage() {
    return this.quote?.proactiveSupportBaseUsage;
  }

  get proactiveSupportMetricPrice() {
    return this.quote?.proactiveSupportMetricPrice;
  }

  get fullProactiveAddonPrice() {
    return this.quote?.fullBreakdownAmount(Number(this.args.plan.idAsNumber)) || 0;
  }

  get isScheduledDowngrade() {
    return (
      this.args.scheduledDowngradeCorePlanId === this.args.plan.idAsNumber &&
      this.args.scheduledDowngradeCorePlanId !== this.args.inSubscriptionPlan.idAsNumber
    );
  }

  get isHighlighted() {
    return this.isSelected && !this.args.isDisabled;
  }

  get planDescription() {
    let descriptionKey = PLAN_DATA[this.args.plan.idAsNumber]?.descriptionTranslationKey;
    if (descriptionKey && this.args.description) {
      return this.args.description;
    }
    if (descriptionKey) {
      return this.intl.t(descriptionKey);
    }
    return this.args.description;
  }

  get linkArticleId() {
    return PLAN_DATA[this.args.plan.idAsNumber]?.linkArticleId;
  }

  get linkArticleUrl() {
    let articleId = this.linkArticleId;
    return articleId ? `https://www.intercom.com/help/articles/${articleId}` : '';
  }

  get linkArticleText() {
    let translationKey = PLAN_DATA[this.args.plan.idAsNumber]?.linkArticleTextTranslationKey;
    return translationKey ? this.intl.t(translationKey) : '';
  }

  get isEssentialPlan() {
    return this.args.plan.id === PRICING_5_X_CORE_ESSENTIAL_ID;
  }

  get shouldShowLiteSeatToFullSeatWarning() {
    return (
      this.appService.app.canSeeEssentialDowngradeWarnings &&
      this.isEssentialPlan &&
      this.liteSeatNumber > 0
    );
  }

  get liteSeatNumber() {
    return this.appService.app.humanAdminsWithLiteSeat.length;
  }

  get hasLiveWorkflows() {
    return this.aiAgentSetupService.totalLiveWorkflowsCount > 0;
  }

  get hasPausedWorkflows() {
    return this.aiAgentSetupService.totalPausedWorkflowsCount > 0;
  }

  get shouldShowPauseWorkflowsWarning() {
    return (
      this.appService.app.canSeeEssentialDowngradeWarnings &&
      this.isEssentialPlan &&
      (this.hasLiveWorkflows || this.hasPausedWorkflows)
    );
  }

  @action
  setSelectedPlan() {
    if (this.args.isDisabled) {
      this.args.showModal(ModalTypes.CannotEditWithScheduledDowngrade);
      return;
    }
    this.args.setSelected(this.args.plan);
  }

  @action
  handleArticleLinkClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: `addon_article_link_${this.args.plan.idAsNumber}`,
      place: 'annual_plans_manage_subscription',
    });

    window.Intercom('showArticle', this.linkArticleId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Billing::AnnualManageSubscription::PlanCard': typeof PlanCard;
  }
}
